import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useToast } from '@/components/ui/use-toast';
import GymMemberFields from '../components/GymMemberFields';
import MembershipCardsSection from '../components/MembershipCardsSection';
import { emptyGymMemberForm, toGymMemberForm } from '../components/gym-member-form';
import { gymMembersService } from '../../services/gymMembers';
import type { GymMember, GymMemberPayload } from '../../types/gym-member';
import { useCurrentGym } from '../hooks/useCurrentGym';
import { useGymAdmin } from '../context/gym-admin-context';

/**
 * Standalone page for one roster row, which is where the "new member"
 * notification sent to gym admins lands.
 */
export default function MemberEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { placePath } = useGymAdmin();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { gym, loading: gymLoading, slug } = useCurrentGym();

  const [member, setMember] = useState<GymMember | null>(null);
  const [form, setForm] = useState<GymMemberPayload>(emptyGymMemberForm);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(
    async (gymSlug: string, memberId: number) => {
      setLoading(true);
      setNotFound(false);
      try {
        const loaded = await gymMembersService.get(gymSlug, memberId);
        setMember(loaded);
        setForm(toGymMemberForm(loaded));
      } catch (error) {
        console.error('Failed to load member:', error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (slug && id) load(slug, Number(id));
  }, [slug, id, load]);

  const canSave = form.name.trim().length > 0;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!slug || !member || !canSave || saving) return;

    setSaving(true);
    try {
      setMember(await gymMembersService.update(slug, member.id, form));
      toast({ title: t('gymAdmin.members.saved') });
    } catch (error) {
      toast({
        title: t('gymAdmin.members.updateError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (gymLoading || loading) return <Skeleton className="h-96 w-full max-w-2xl" />;

  if (notFound || !member || !slug) {
    return (
      <div className="max-w-2xl space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(placePath('/members'))}>
          <ArrowLeft className="mr-2 size-4" />
          {t('gymAdmin.members.backToList')}
        </Button>
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            {t('gymAdmin.members.notFound')}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      <Button variant="ghost" size="sm" onClick={() => navigate(placePath('/members'))}>
        <ArrowLeft className="mr-2 size-4" />
        {t('gymAdmin.members.backToList')}
      </Button>

      <Card>
        <CardContent className="flex flex-wrap items-center gap-4 p-4">
          {member.linked && (
            <Avatar className="size-12">
              <AvatarImage src={member.user_avatar_small_url ?? undefined} alt="" />
              <AvatarFallback>{member.name.charAt(0).toUpperCase()}</AvatarFallback>
            </Avatar>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="truncate text-lg font-medium">{member.full_name}</h2>
              {member.linked && <Badge>{t("gymAdmin.members.linked")}</Badge>}
              {member.invited && (
                <Badge variant="secondary">{t("gymAdmin.members.invited")}</Badge>
              )}
              {member.minor && (
                <Badge variant="outline" title={t('gymAdmin.members.minorHint')}>
                  {t('gymAdmin.members.minor')}
                </Badge>
              )}
              {member.medical_certificate_expired && (
                <Badge variant="destructive" className="gap-1">
                  <AlertTriangle className="size-3" />
                  {t('gymAdmin.members.certificateExpired')}
                </Badge>
              )}
            </div>
            {member.user_username && (
              <p className="mt-1 text-sm text-muted-foreground">@{member.user_username}</p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <p className="mb-4 text-sm text-muted-foreground">
            {t('gymAdmin.members.formDescription')}
          </p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <GymMemberFields form={form} onChange={setForm} idPrefix="member-edit" />

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => navigate(placePath('/members'))}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={!canSave || saving}>
                {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
                {t('common.save')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <MembershipCardsSection gymSlug={slug} gymName={gym?.name ?? ''} memberId={member.id} />
    </div>
  );
}
